import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { clearToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
      // On ne purge le jeton que sur un vrai 401 (auth invalide), pas sur une coupure réseau.
      if (e?.response?.status === 401) clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    setUser(null);
    window.location.href = "/";
  };

  // Migration du bilan gratuit (fait sans compte) vers le profil, dès qu'un compte client est prêt.
  const migratingBilan = useRef(false);
  useEffect(() => {
    if (!user || user.role === "coach" || user.role === "admin") return;
    const raw = localStorage.getItem("mai_bilan_answers");
    if (!raw || migratingBilan.current) return;
    migratingBilan.current = true;
    (async () => {
      try {
        await api.post("/onboarding/apply-bilan", JSON.parse(raw));
        // On n'efface le bilan qu'APRÈS un succès : jamais de perte silencieuse.
        localStorage.removeItem("mai_bilan_answers");
      } catch {
        // Échec réseau/serveur : on conserve le bilan pour réessayer au prochain chargement.
      } finally {
        migratingBilan.current = false;
      }
    })();
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
